import { useCallback, useEffect, useRef, useState } from "react";
import {
  askAssistant,
  assistantErrorKey,
  isConversationExpired,
  type AssistantAnswer,
} from "../../../api/assistant";

export interface AssistantTurn {
  key: string;
  question: string;
  /** `Date.now()` при изпращане — за брояча на секундите, докато се чака. */
  startedAt: number;
  status: "pending" | "done" | "error";
  answer?: AssistantAnswer;
  /** i18n ключ на грешката (`monitoring.assistantErr*`). */
  errorKey?: string;
}

/**
 * Един разговор с помощника. Историята е при OpenAI — тук се пази само
 * `conversationId` (id-то на последния отговор), който BE-то връща и който
 * се подава със следващия въпрос. Сървърното състояние не е в React Query:
 * разговорът живее само докато страницата е отворена.
 */
export function useAssistantChat() {
  const [turns, setTurns] = useState<AssistantTurn[]>([]);
  const conversationId = useRef<string | null>(null);
  // Отговор, пристигнал след „Нов разговор" или след напускане на страницата,
  // не бива да се появи в новия разговор.
  const generation = useRef(0);

  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );

  const pending = turns.some((turn) => turn.status === "pending");

  const ask = useCallback(
    async (rawQuestion: string) => {
      const question = rawQuestion.trim();
      if (!question || pending) return;
      const myGeneration = generation.current;
      const key = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setTurns((current) => [
        ...current,
        { key, question, startedAt: Date.now(), status: "pending" },
      ]);
      const update = (patch: Partial<AssistantTurn>) =>
        setTurns((current) =>
          current.map((turn) => (turn.key === key ? { ...turn, ...patch } : turn)),
        );
      try {
        const answer = await askAssistant(question, conversationId.current);
        if (generation.current !== myGeneration) return;
        conversationId.current = answer.conversationId || null;
        update({ status: "done", answer });
      } catch (error) {
        if (generation.current !== myGeneration) return;
        // Изтекъл разговор при OpenAI → следващият въпрос започва наново.
        if (isConversationExpired(error)) conversationId.current = null;
        update({ status: "error", errorKey: assistantErrorKey(error) });
      }
    },
    [pending],
  );

  const reset = useCallback(() => {
    generation.current += 1;
    conversationId.current = null;
    setTurns([]);
  }, []);

  return { turns, pending, ask, reset };
}
